import { useConfiguratorStore } from '@store/useConfiguratorStore';
import type { LogoInstance } from '@/types';
// import { getModelConfig } from '@/config/models';
import './LogoGallery.css';

export default function LogoGallery() {
    const {
        logos,
        addLogoInstance,
        activePrintAreaId,
        printAreas,
        removeLogo,
        setActivePrintArea,
        currentModel,
        // glbUrl
    } = useConfiguratorStore();

    const handleLogoClick = (logoId: string) => {
        
        // Safety check: ensure printAreas is available
        if (!printAreas || printAreas.length === 0) {
            alert('No print areas available for this model');
            return;
        }
        
        // Auto-select first print area if none is selected
        let targetPrintAreaId = activePrintAreaId;
        if (!targetPrintAreaId && printAreas.length > 0) {
            targetPrintAreaId = printAreas[0].id;
            setActivePrintArea(targetPrintAreaId); // Sync UI
        }
        
        if (!targetPrintAreaId) {
            alert('No print areas available for this model');
            return;
        }

        // Check if "All Faces" is selected
        const isAllFaces = targetPrintAreaId === 'all-faces';
        
        if (isAllFaces) {
            // For Square Pen "All Faces", Face 1 (front) is located at U=0.375
            const initialX = (currentModel === 'square-pen') ? 0.375 : 0.5;

            // Create a single instance assigned to the `all-faces` print area
            const newInstance: Omit<LogoInstance, 'layerOrder' | 'name'> = {
                id: `instance-${Date.now()}`,
                logoId,
                printAreaId: 'all-faces',
                position: { x: initialX, y: 0.5 },
                scale: 1,
                rotation: (currentModel === 'pen' || currentModel === 'square-pen' || currentModel === 'nylon-lanyard' || currentModel === 'dye-sublimated-lanyard-with-metal-crimp') ? 90 : 0,
                effect: 'none',
                timestamp: Date.now(),
            };

            addLogoInstance(newInstance);
        } else {
            // For individual areas, add single instance as before
            const targetPrintArea = printAreas.find(pa => pa.id === targetPrintAreaId);
            let initialPosition = { x: 0.5, y: 0.5 };

            if (targetPrintArea && targetPrintArea.uvBounds) {
                const { minU, maxU, minV, maxV, needsFlipY } = targetPrintArea.uvBounds;
                
                let centerU = (minU + maxU) / 2;
                let centerV = (minV + maxV) / 2;
                
                centerU = ((centerU % 1) + 1) % 1; 
                centerV = ((centerV % 1) + 1) % 1;

                const useInvertX = targetPrintArea.invertX ?? false;
                const useInvertY = targetPrintArea.invertY ?? needsFlipY ?? false;

                const x = useInvertX ? (1 - centerU) : centerU;
                const y = useInvertY ? centerV : (1 - centerV);

                initialPosition = { x, y };
            }

            const newInstance: Omit<LogoInstance, 'layerOrder' | 'name'> = {
                id: `instance-${Date.now()}`,
                logoId,
                printAreaId: targetPrintAreaId,
                position: initialPosition,
                scale: 1,
                rotation: (currentModel === 'pen' || currentModel === 'square-pen' || currentModel === 'nylon-lanyard' || currentModel === 'dye-sublimated-lanyard-with-metal-crimp') ? 90 : 0,
                effect: 'none',
                timestamp: Date.now(),
            };

            addLogoInstance(newInstance);
        }
    };

    const handleDeleteLogo = (logoId: string, logoName: string, e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent logo click when deleting
        
        if (confirm(`Delete "${logoName}"? This will also remove it from all print areas.`)) {
            removeLogo(logoId);
        }
    };

    if (logos.length === 0) {
        return null;
    }

    return (
        <div className="logo-gallery">
            {logos.map((logo) => (
                <div key={logo.id} className="logo-item">
                    <img
                        src={logo.processedImage}
                        alt={logo.name}
                        className="logo-thumbnail"
                        onClick={() => handleLogoClick(logo.id)}
                        title={`Click to add ${logo.name} to print area`}
                    />
                    <button 
                        className="logo-delete-btn"
                        onClick={(e) => handleDeleteLogo(logo.id, logo.name, e)}
                        title="Delete logo"
                        aria-label="Delete logo"
                    >
                        ×
                    </button>
                </div>
            ))}
        </div>
    );
}
